import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog, infoDialog } from "../components/confirm-dialog";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import { showToast } from "../utils/toast";
import { stageLabel } from "../utils/stage";
import type { Subject, Lecturer, CourseModel, SubjectCurriculumInfo, Curriculum } from "../types";
import { esc } from "../utils/html";
import { openCsvImportModal } from "../components/csv-import-modal";

/**
 * Fächer der Schule: Name, Kürzel, Farbe, Ansprechpartner. Ein Fach gibt es einmal je
 * Schule. In welchem Ausbildungsmodell es läuft, in welcher Stufe und mit wie vielen UE,
 * steht im Lehrplan des Modells; hier steht davon nur die Übersicht.
 *
 * Mehrere Fächer lassen sich markieren und auf einmal einem Ausbildungsmodell zuordnen.
 * Das ist der schnelle Weg, einen Lehrplan zu füllen, wenn die Fächer schon angelegt sind.
 */

let subjects: Subject[] = [];
let lecturers: Lecturer[] = [];
let courseModels: CourseModel[] = [];
let currentEl: HTMLElement | null = null;
let searchQuery = "";
const selected = new Set<number>();

async function load() {
  [subjects, lecturers, courseModels] = await Promise.all([
    api.stundenplan.subjects.list(),
    api.stundenplan.lecturers.list(),
    api.stundenplan.courseModels.list(),
  ]);
  // Gelöschte Fächer fallen aus der Auswahl.
  for (const id of [...selected]) if (!subjects.some(s => s.id === id)) selected.delete(id);
}

function fmt(n: number): string {
  return (Math.round(n * 100) / 100).toLocaleString("de-DE");
}

/** „PT Vollzeit · 1. Ausbildungsjahr · 20 UE" */
function curriculumLabel(c: SubjectCurriculumInfo): string {
  return [c.modelName, c.stageLabel || "alle Stufen", c.schuleUe !== null ? `${fmt(c.schuleUe)} UE` : null]
    .filter(Boolean).join(" · ");
}

function filtered(): Subject[] {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return subjects;
  return subjects.filter(s => [s.name, s.shortName, ...s.curricula.map(c => c.modelName)]
    .some(v => (v || "").toLowerCase().includes(q)));
}

function renderTable() {
  const tbody = currentEl?.querySelector("tbody");
  if (!tbody) return;
  const list = filtered();
  const cols = canWrite() ? 5 : 3;
  if (list.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${cols}" class="empty-state">${searchQuery.trim() ? "Keine Treffer" : "Keine Fächer vorhanden"}</td></tr>`;
  } else {
    tbody.innerHTML = list.map(s => `
      <tr class="fa-row${selected.has(s.id) ? " is-selected" : ""}" data-open="${s.id}">
        ${writeOnly(`<td class="fa-check"><input type="checkbox" data-sel="${s.id}" ${selected.has(s.id) ? "checked" : ""} aria-label="${esc(s.name)} auswählen" /></td>`)}
        <td>
          <span class="lp-swatch" style="background:${esc(s.color)}"></span><span class="lp-name">${esc(s.name)}</span>${s.shortName ? `<span class="lp-short">${esc(s.shortName)}</span>` : ""}
          ${s.active ? "" : `<span class="badge badge-gray" style="margin-left:.4rem">inaktiv</span>`}
        </td>
        <td class="fa-curricula">${s.curricula.length === 0
          ? `<span class="fa-none">in keinem Lehrplan</span>`
          : s.curricula.map(c => `<div>${esc(curriculumLabel(c))}</div>`).join("")}</td>
        <td class="text-muted">${esc(s.contactLecturerName || "")}</td>
        ${writeOnly(`<td class="fa-actions"><button class="btn btn-danger btn-sm" data-del="${s.id}">Löschen</button></td>`)}
      </tr>`).join("");
  }
  updateSelection();
}

/** Aktion und Kopf-Checkbox an die Auswahl anpassen. */
function updateSelection() {
  const el = currentEl;
  if (!el) return;
  const visible = filtered().map(s => s.id);
  const all = el.querySelector<HTMLInputElement>("#fa-sel-all");
  if (all) {
    const n = visible.filter(id => selected.has(id)).length;
    all.checked = n > 0 && n === visible.length;
    all.indeterminate = n > 0 && n < visible.length;
  }
  for (const btn of el.querySelectorAll<HTMLButtonElement>("#fa-assign, #fa-delete")) btn.disabled = selected.size === 0;
}

// ── Fenster ──

function openForm(subject?: Subject) {
  const isEdit = !!subject;
  const readOnly = !canWrite();
  const disabled = readOnly ? " disabled" : "";

  const lecturerOpts = `<option value="">Kein Ansprechpartner</option>` +
    lecturers.map(l => `<option value="${l.id}" ${subject?.contactLecturerId === l.id ? "selected" : ""}>${esc(`${l.firstName} ${l.lastName}`.trim())}</option>`).join("");

  openModal(isEdit ? (readOnly ? "Fach" : "Fach bearbeiten") : "Neues Fach", `<div class="lp-form">
    <div class="lp-new-grid">
      <div class="form-group"><label for="f-name">Bezeichnung *</label><input id="f-name" value="${esc(subject?.name || "")}"${disabled} /></div>
      <div class="form-group"><label for="f-short">Kürzel</label><input id="f-short" value="${esc(subject?.shortName || "")}"${disabled} /></div>
      <div class="form-group"><label for="f-color">Farbe</label><input type="color" id="f-color" value="${esc(subject?.color || "#2563eb")}"${disabled} /></div>
    </div>
    <div class="form-grid">
      <div class="form-group"><label for="f-lecturer">Ansprechpartner</label><select id="f-lecturer"${disabled}>${lecturerOpts}</select></div>
      <div class="form-group" style="justify-content:flex-end">
        <label class="checkbox-label"><input type="checkbox" id="f-active" ${subject?.active !== false ? "checked" : ""}${disabled} /> Aktiv</label>
      </div>
    </div>
    ${isEdit ? `<div class="form-group">
      <label>Steht in diesen Lehrplänen</label>
      ${subject!.curricula.length === 0
        ? `<div class="lp-muted" style="font-size:.875rem">In keinem. Zuordnen geht im Lehrplan eines Ausbildungsmodells oder hier in der Liste über die Auswahl.</div>`
        : `<div class="fa-curricula-list">${subject!.curricula.map(c => `<div>${esc(curriculumLabel(c))}</div>`).join("")}</div>`}
    </div>` : ""}</div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="f-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="f-save">Speichern</button>`)}
    </div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  if (readOnly) { lockInputs(mb); return; }

  mb.querySelector("#f-save")!.addEventListener("click", async () => {
    const data = {
      name: (mb.querySelector("#f-name") as HTMLInputElement).value.trim(),
      shortName: (mb.querySelector("#f-short") as HTMLInputElement).value.trim() || null,
      color: (mb.querySelector("#f-color") as HTMLInputElement).value,
      contactLecturerId: (mb.querySelector("#f-lecturer") as HTMLSelectElement).value || null,
      active: (mb.querySelector("#f-active") as HTMLInputElement).checked,
    };
    if (!data.name) { showModalError(mb, "Bezeichnung ist erforderlich"); return; }

    const saveBtn = mb.querySelector("#f-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      if (isEdit) await api.stundenplan.subjects.update(subject!.id, data);
      else await api.stundenplan.subjects.create(data);
      closeModal();
      await load();
      renderTable();
      showToast("Fach gespeichert");
    } catch (err: any) {
      showModalError(mb, err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });
  (mb.querySelector("#f-name") as HTMLInputElement).focus();
}

/**
 * Markierte Fächer einem Ausbildungsmodell zuordnen. Gruppe und Stufe gelten für alle;
 * UE trägt man danach im Lehrplan je Fach ein. Was dort schon steht, bleibt unverändert.
 */
function openAssignDialog() {
  const ids = [...selected];
  if (ids.length === 0) return;
  if (courseModels.length === 0) {
    infoDialog("Kein Ausbildungsmodell", "Lege zuerst unter „Ausbildungsmodelle“ ein Modell an. Jedes Modell enthält einen Lehrplan, in den du die Fächer dann aufnimmst.");
    return;
  }
  const names = subjects.filter(s => selected.has(s.id)).map(s => s.name);
  openModal(`${ids.length} ${ids.length === 1 ? "Fach" : "Fächer"} zuordnen`, `<div class="lp-form">
    <p class="lp-modal-lead">${esc(names.slice(0, 6).join(", "))}${names.length > 6 ? ` und ${names.length - 6} weitere` : ""}</p>
    <div class="form-group">
      <label for="a-model">Ausbildungsmodell *</label>
      <select id="a-model">
        <option value="">Modell wählen</option>
        ${courseModels.map(m => `<option value="${m.id}">${esc(m.name)}</option>`).join("")}
      </select>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label for="a-group">Fächergruppe</label>
        <select id="a-group" disabled><option value="">Keine Gruppe</option></select>
      </div>
      <div class="form-group">
        <label for="a-stage">Stufe</label>
        <select id="a-stage" disabled><option value="">Alle Stufen</option></select>
      </div>
    </div>
    <p class="lp-modal-lead" id="a-hint">Fächer, die schon im Lehrplan des Modells stehen, werden übersprungen. Die UE trägst du danach im Lehrplan ein.</p></div>
    <div class="alert alert-error hidden" id="f-modal-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="a-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="a-save" disabled>Zuordnen</button>
    </div>`);

  const mb = modalBody();
  const modelSelect = mb.querySelector("#a-model") as HTMLSelectElement;
  const groupSelect = mb.querySelector("#a-group") as HTMLSelectElement;
  const stageSelect = mb.querySelector("#a-stage") as HTMLSelectElement;
  const saveBtn = mb.querySelector("#a-save") as HTMLButtonElement;
  const hint = mb.querySelector("#a-hint") as HTMLElement;
  let plan: Curriculum | null = null;

  // Gruppen und Stufen hängen am gewählten Modell.
  modelSelect.addEventListener("change", async () => {
    const model = courseModels.find(m => m.id === +modelSelect.value);
    plan = null;
    groupSelect.disabled = stageSelect.disabled = saveBtn.disabled = !model;
    groupSelect.innerHTML = `<option value="">Keine Gruppe</option>`;
    stageSelect.innerHTML = `<option value="">Alle Stufen</option>`;
    if (!model) return;
    stageSelect.innerHTML += Array.from({ length: model.stageCount }, (_, i) =>
      `<option value="${i + 1}">${esc(stageLabel(model, i + 1))}</option>`).join("");
    try {
      plan = await api.stundenplan.curriculum.get(model.id);
      groupSelect.innerHTML += plan.groups.map(g => `<option value="${g.id}">${esc(g.name)}</option>`).join("");
      const already = plan.subjects.filter(x => selected.has(x.subjectId)).length;
      hint.textContent = already > 0
        ? `${already} der ${ids.length} Fächer ${already === 1 ? "steht" : "stehen"} schon im Lehrplan von „${model.name}“ und ${already === 1 ? "wird" : "werden"} übersprungen. Die UE trägst du danach im Lehrplan ein.`
        : "Die UE trägst du danach im Lehrplan des Modells ein.";
    } catch { /* Gruppen sind Komfort, die Zuordnung geht auch ohne */ }
  });

  mb.querySelector("#a-cancel")!.addEventListener("click", closeModal);
  saveBtn.addEventListener("click", async () => {
    const modelId = +modelSelect.value;
    if (!modelId) return;
    saveBtn.disabled = true; saveBtn.textContent = "Zuordnen…";
    try {
      const result = await api.stundenplan.curriculum.addSubjects(modelId, {
        subjectIds: ids,
        groupId: +groupSelect.value || null,
        stage: +stageSelect.value || null,
      });
      closeModal();
      selected.clear();
      await load();
      renderTable();
      const name = courseModels.find(m => m.id === modelId)?.name ?? "";
      showToast(`${result.added} ${result.added === 1 ? "Fach" : "Fächer"} in „${name}“ aufgenommen${result.skipped ? `, ${result.skipped} standen schon drin` : ""}`);
    } catch (err: any) {
      showModalError(mb, err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Zuordnen";
    }
  });
}

/**
 * Markierte Fächer löschen. Verplante Fächer lehnt das Backend ab wie beim einzelnen
 * Löschen; sie bleiben markiert, damit man sie gleich auf inaktiv setzen kann.
 */
async function deleteSelected() {
  const ids = [...selected];
  if (ids.length === 0) return;
  const chosen = subjects.filter(s => selected.has(s.id));
  const inPlans = chosen.filter(s => s.curricula.length > 0).length;
  const names = chosen.map(s => s.name);
  const ok = await confirmDialog({
    title: `${ids.length} ${ids.length === 1 ? "Fach" : "Fächer"} löschen`,
    message: `${names.slice(0, 6).join(", ")}${names.length > 6 ? ` und ${names.length - 6} weitere` : ""} ${ids.length === 1 ? "wird" : "werden"} gelöscht`
      + `${inPlans ? `, ${inPlans === 1 ? "eines davon steht" : `${inPlans} davon stehen`} in Lehrplänen und ${inPlans === 1 ? "wird" : "werden"} dort herausgenommen` : ""}.`
      + "\n\nFächer, die im Stundenplan schon verplant sind, bleiben stehen.",
    confirmLabel: "Löschen", danger: true,
  });
  if (!ok) return;
  try {
    const result = await api.stundenplan.subjects.deleteMany(ids);
    await load();
    renderTable();
    if (result.blocked.length === 0) {
      showToast(`${result.deleted} ${result.deleted === 1 ? "Fach" : "Fächer"} gelöscht`);
      return;
    }
    const list = result.blocked.map(b => `${b.name} (${b.lessonCount} ${b.lessonCount === 1 ? "Termin" : "Termine"})`).join("\n");
    await infoDialog(
      result.deleted ? "Nicht alle Fächer gelöscht" : "Fächer werden noch verwendet",
      `${result.deleted ? `${result.deleted} ${result.deleted === 1 ? "Fach wurde" : "Fächer wurden"} gelöscht. ` : ""}`
        + `${result.blocked.length === 1 ? "Dieses Fach ist" : "Diese Fächer sind"} im Stundenplan verplant und ${result.blocked.length === 1 ? "bleibt" : "bleiben"}:\n${list}\n\n`
        + (result.blocked.length === 1
          ? "Setze es stattdessen auf inaktiv, dann bleibt der bisherige Plan erhalten. Es ist noch markiert."
          : "Setze sie stattdessen auf inaktiv, dann bleibt der bisherige Plan erhalten. Sie sind noch markiert."),
    );
  } catch (err: any) { showToast("Fehler: " + err.message); }
}

export async function render(el: HTMLElement) {
  el.innerHTML = `<div class="fa-page"></div>`;
  const page = el.querySelector(".fa-page") as HTMLElement;
  currentEl = page;
  searchQuery = "";
  selected.clear();
  page.innerHTML = `
    <div class="settings-page-header">
      <div>
        <h1>Fächer</h1>
        <p>Alle Fächer der Schule. In welchem Ausbildungsmodell ein Fach läuft, in welcher Stufe und mit wie vielen UE, steht im Lehrplan des Modells. Markierte Fächer lassen sich hier auf einmal einem Modell zuordnen oder löschen.</p>
      </div>
      ${writeOnly(`<div class="settings-page-actions">
        <button class="btn btn-secondary" id="btn-csv">CSV-Import</button>
        <button class="btn btn-primary" id="btn-add">+ Neues Fach</button>
      </div>`)}
    </div>
    ${readOnlyNotice()}
    <div class="filter-bar">
      <input type="search" id="search-query" data-readable="true" placeholder="Suchen …" />
      ${writeOnly(`<button class="btn btn-secondary btn-sm fa-assign" id="fa-assign" disabled>Ausbildungsmodell zuordnen …</button>
        <button class="btn btn-secondary btn-sm fa-delete" id="fa-delete" disabled>Löschen</button>`)}
    </div>
    <div class="card table-wrap">
      <table class="fa-table">
        <thead><tr>
          ${writeOnly(`<th class="fa-check"><input type="checkbox" id="fa-sel-all" aria-label="Alle auswählen" /></th>`)}
          <th>Bezeichnung</th>
          <th>Lehrpläne</th>
          <th style="width:180px">Ansprechpartner</th>
          ${writeOnly(`<th style="width:100px"></th>`)}
        </tr></thead>
        <tbody></tbody>
      </table>
    </div>`;

  try {
    await load();
  } catch (err: any) {
    page.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  renderTable();

  page.querySelector("#btn-add")?.addEventListener("click", () => openForm());
  page.querySelector("#fa-assign")?.addEventListener("click", openAssignDialog);
  page.querySelector("#fa-delete")?.addEventListener("click", deleteSelected);
  page.querySelector("#btn-csv")?.addEventListener("click", () => openCsvImportModal({
    title: "Fächer importieren",
    duplicateHint: "Vorhandene Fächer werden am Namen erkannt und nicht verändert. Mit einem Ausbildungsmodell kommt das Fach zusätzlich in dessen Lehrplan; steht es dort schon, bleibt die Zeile, wie sie ist.",
    fieldDescriptions: [
      { field: "Fach", hint: "Pflichtfeld, außer in einer Zeile, die nur die Pflicht einer Fächergruppe setzt" },
      { field: "Kürzel", hint: "Optional, Beschriftung im Wochenraster" },
      { field: "Farbe", hint: "Optional, als Hexwert wie #2563eb. Ohne Angabe vergibt Meduvio eine Farbe" },
      { field: "Ausbildungsmodell", hint: "Optional, Name eines vorhandenen Modells, zum Beispiel „Physiotherapie Vollzeit\". Ohne Modell wird nur das Fach angelegt. Dasselbe Fach darf für mehrere Modelle in je einer Zeile stehen" },
      { field: "Fächergruppe", hint: "Optional, zum Beispiel „Anatomie\". Eine Zeile nur mit Modell, Fächergruppe und UE Verordnung setzt die Pflicht der Gruppe" },
      { field: "Stufe", hint: "Optional, Nummer der Stufe im Modell (1, 2, 3 …). Leer für alle Stufen" },
      { field: "UE Schule", hint: "Optional, was die Schule ansetzt. Ziel für „Fach fertig\" im Stundenplan" },
      { field: "UE Verordnung", hint: "Optional. Mit Fächergruppe gilt der Wert für die Gruppe, ohne für das Fach" },
      { field: "UE pro Woche", hint: "Optional, für den Vorschlag des Serienzeitraums im Stundenplan" },
      { field: "Ansprechpartner", hint: "Optional, Vor- und Nachname oder eindeutiger Nachname eines Dozenten" },
    ],
    onDownloadTemplate: () => api.stundenplan.subjects.csvTemplate(),
    onImport: (file) => api.stundenplan.subjects.csvImport(file),
    onComplete: async () => { await load(); renderTable(); },
  }));
  const searchInput = page.querySelector("#search-query") as HTMLInputElement;
  searchInput.addEventListener("input", () => { searchQuery = searchInput.value; renderTable(); });

  page.querySelector("#fa-sel-all")?.addEventListener("change", (e) => {
    const on = (e.target as HTMLInputElement).checked;
    for (const s of filtered()) { if (on) selected.add(s.id); else selected.delete(s.id); }
    renderTable();
  });

  page.querySelector("tbody")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    // Die Checkbox-Zelle wählt aus, statt das Fach zu öffnen.
    const checkCell = target.closest(".fa-check") as HTMLElement | null;
    if (checkCell) {
      const box = checkCell.querySelector("input") as HTMLInputElement;
      if (target !== box) box.checked = !box.checked;
      const id = +box.dataset.sel!;
      if (box.checked) selected.add(id); else selected.delete(id);
      box.closest("tr")?.classList.toggle("is-selected", box.checked);
      updateSelection();
      return;
    }

    const del = target.closest("[data-del]") as HTMLElement | null;
    if (del) {
      const subject = subjects.find(s => s.id === +del.dataset.del!);
      if (!subject) return;
      const inPlans = subject.curricula.length;
      const ok = await confirmDialog({
        title: "Fach löschen",
        message: `„${subject.name}“ wird gelöscht${inPlans ? ` und aus ${inPlans} ${inPlans === 1 ? "Lehrplan" : "Lehrplänen"} genommen` : ""}.`,
        confirmLabel: "Löschen", danger: true,
      });
      if (!ok) return;
      try {
        await api.stundenplan.subjects.delete(subject.id);
        selected.delete(subject.id);
        await load();
        renderTable();
        showToast("Fach gelöscht");
      } catch (err: any) {
        // Verplante Fächer lehnt das Backend mit 409 ab. Die Begründung nennt die Zahl
        // der betroffenen Einheiten und gehört in einen Dialog, nicht in einen Toast.
        if (err.status === 409) await infoDialog("Fach wird noch verwendet", err.message);
        else showToast("Fehler: " + err.message);
      }
      return;
    }

    const row = target.closest("[data-open]") as HTMLElement | null;
    if (row) {
      const subject = subjects.find(s => s.id === +row.dataset.open!);
      if (subject) openForm(subject);
    }
  });
}

function showModalError(mb: HTMLElement, message: string) {
  const box = mb.querySelector("#f-modal-error") as HTMLElement | null;
  if (!box) { showToast(message); return; }
  box.textContent = message;
  box.classList.remove("hidden");
}
